import { useEffect, useState } from 'react';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import {
  DatabaseUserDocumentType,
  UserDocument,
  UserDocumentType,
  UserDriverLic,
  UserDriverLicRequest,
} from '../../clients/quipli-service/user/user';
import { useCustomer } from '../../hooks/use-customer-context';
import { EcommerceButton } from '../../components/form/button';
import { Typography } from '../../components/typography/typography';
import { DateTime } from 'luxon';
import { Field, FileField } from '../../components/field/field';
import { DatePicker } from '../../components/date-picker/date-picker';
import { SelectForm } from '../../components/select-form/select-form';
import { Card } from '../../components/card/card';
import { Divider } from '../../components/divider/divider';
import { ecommerceToast } from '../../util/toasts';
import { Spinner } from '../../components/spinner/spinner';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import { LicenseDetails } from './license-details';
import { StateProvince } from '../../clients/quipli-service/locales/locales';

export const LicenseWrapper = ({
  isEditing,
  isRequired,
  conf,
  setIsEditing,
  onSubmit,
}: {
  isEditing: boolean;
  isRequired: boolean;
  conf: Configuration[];
  setIsEditing: (v: boolean) => void;
  onSubmit: () => void;
}) => {
  const { getUserDriverLicense, getUserDocumentByType } = useCustomerAPI();

  const [licenseDetails, setLicenseDetails] = useState<UserDriverLic>();
  const [licenseFile, setLicenseFile] = useState<UserDocument>();
  const [licenseScanEnabled] = useState<boolean>(!!conf.find((c) => c.variable === 'CONF_IDSCAN_LICENSE_KEY')?.value);
  const [licenseUpdateRequired, setLicenseUpdateRequired] = useState<boolean>(false);
  const existsLegacyDocument = licenseDetails || licenseFile;

  const fetchDocs = async () => {
    const [details, file] = await Promise.all([
      getUserDriverLicense(),
      getUserDocumentByType(UserDocumentType.License),
    ]);

    setLicenseDetails(details);
    setLicenseFile(file);
    setLicenseUpdateRequired(details === undefined || details.expirationDate < DateTime.local());

    if ((licenseScanEnabled && !licenseUpdateRequired) || details || file) {
      onSubmit();
    }
  };

  useEffect(() => {
    fetchDocs();
  }, []);

  return (
    <>
      <div className="pt-4 px-3">
        <Typography type="text-sm">
          {licenseScanEnabled
            ? licenseUpdateRequired &&
              "A scan of your driver's license is required to complete the order. You will receive a text message with a link to scan your driver's license once you complete the order."
            : !existsLegacyDocument &&
              `${!isRequired ? 'Optional: ' : ''}Please upload your Driver's License${
                isRequired ? ' in order to be able to place your order request with us' : ''
              }.`}
        </Typography>
      </div>

      <div>
        {isEditing ? (
          <LicenseForm
            existingDetails={licenseDetails}
            onSubmit={async () => {
              await fetchDocs();
              setIsEditing(false);
              onSubmit();
            }}
            onCancel={() => {
              setIsEditing(false);
              if (existsLegacyDocument) {
                onSubmit();
              }
            }}
          />
        ) : (
          <>
            {licenseScanEnabled ? (
              !licenseUpdateRequired && (
                <div className="px-3 py-4">
                  <div className="w-full flex flex-col gap-2">
                    <LicenseDetails userLicenseDetails={licenseDetails} userLicenseFile={licenseFile} />
                  </div>
                </div>
              )
            ) : existsLegacyDocument ? (
              <div className="px-3 py-4">
                <div className="w-full flex flex-col gap-2">
                  <LicenseDetails userLicenseDetails={licenseDetails} userLicenseFile={licenseFile} />
                </div>
              </div>
            ) : (
              <div className="p-3">
                <EcommerceButton
                  buttonStyle="tertiary"
                  onClick={() => {
                    setIsEditing(true);
                  }}
                >
                  <Typography type="text-sm" weight="medium">
                    Add License
                  </Typography>
                </EcommerceButton>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

const LicenseForm = ({
  existingDetails,
  onCancel,
  onSubmit,
}: {
  existingDetails?: UserDriverLic;
  onCancel: () => void;
  onSubmit?: () => Promise<void>;
}) => {
  const customer = useCustomer();
  const { listStateProvinces, createUserDriverLicense, updateUserDriverLicense, createUpdateUserDocument } =
    useCustomerAPI();

  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const [name, setName] = useState<string>(existingDetails?.fullName || customer?.name || '');
  const [licenseNumber, setLicenseNumber] = useState<string>(existingDetails?.driverLicNum || '');
  const [expirationDate, setExpirationDate] = useState<DateTime | undefined>(existingDetails?.expirationDate);
  const [dateOfBirth, setDateOfBirth] = useState<DateTime | undefined>(existingDetails?.dateOfBirth);
  const [stateProvince, setStateProvince] = useState<StateProvince>();
  const [states, setStates] = useState<StateProvince[]>([]);

  const [inputFile, setInputFile] = useState<File>();
  const isFieldRequired = !inputFile;

  const isButtonDisabled = !inputFile && (!name || !licenseNumber || !expirationDate || !expirationDate.isValid);

  const handleSubmit = async () => {
    if (isButtonDisabled || !customer) return;

    setIsRequestInProgress(true);
    const hasDetails = name && licenseNumber && expirationDate && expirationDate.isValid;

    if (inputFile) {
      try {
        await createUpdateUserDocument(
          inputFile,
          customer.id.toString(),
          DatabaseUserDocumentType.License.toString(),
          true,
        );

        if (!hasDetails) {
          ecommerceToast('Saved successfully.', 'success');
          await onSubmit?.();
          return;
        }
      } catch (error: any) {
        ecommerceToast(error.message || 'Something went wrong.', 'error');
        setIsRequestInProgress(false);
        return;
      }
    }

    if (!hasDetails) {
      return;
    }

    const req: UserDriverLicRequest = {
      fullName: name,
      driverLicNum: licenseNumber,
      expirationDate: expirationDate,
      dateOfBirth: dateOfBirth,
      stateId: stateProvince?.id,
    };
    try {
      await (existingDetails ? updateUserDriverLicense(req) : createUserDriverLicense(req));

      ecommerceToast('Saved successfully.', 'success');
      await onSubmit?.();
    } catch (error: any) {
      ecommerceToast(error.message || 'Something went wrong.', 'error');
    } finally {
      setIsRequestInProgress(false);
    }
  };

  useEffect(() => {
    listStateProvinces().then((_states) => {
      setStates(_states);
      if (existingDetails) {
        setStateProvince(_states.find((s) => s.id === existingDetails.state?.id));
      }
    });
  }, []);

  return (
    <>
      <div className="py-4 px-3">
        <Card className="max-w-md bg-grey-25 !rounded-lg p-3 flex flex-col gap-2">
          <Field type="text" label="Full name" value={name} onChange={setName} isRequired={isFieldRequired} />

          <DatePicker label="Date of Birth" value={dateOfBirth} onChange={setDateOfBirth} />

          <Field
            type="text"
            label="Driver's License Number"
            value={licenseNumber}
            onChange={setLicenseNumber}
            isRequired={isFieldRequired}
          />

          <SelectForm
            label="Issuing State/Province"
            options={states}
            value={stateProvince}
            onChange={(option) => setStateProvince(option ?? undefined)}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => `${o.id}`}
          />

          <DatePicker
            label="Expiration Date"
            isRequired={isFieldRequired}
            value={expirationDate}
            onChange={setExpirationDate}
          />

          <Divider text="OR" />

          <FileField
            className="[&>label]:!block"
            onChange={(file) => setInputFile(file as File)}
            value={inputFile}
            id="insurance-file"
          />
        </Card>
      </div>

      <Divider />

      {isRequestInProgress ? (
        <Spinner />
      ) : (
        <div className="p-3 flex justify-start gap-2">
          <EcommerceButton buttonStyle="tertiary" onClick={onCancel}>
            Cancel
          </EcommerceButton>
          <EcommerceButton
            buttonStyle="primary"
            disabled={isButtonDisabled}
            onClick={handleSubmit}
            dataCy="license_save"
          >
            Save
          </EcommerceButton>
        </div>
      )}
    </>
  );
};
